import { SERVICES } from '@autopass-web/domain';

import Client from '../client';
import paymentError from './utils/payment-error';

import type { PaymentInfo, Receipt } from './types';

export const applePayConfig = (amount: string) => {
  return {
    countryCode: 'TW',
    currencyCode: 'TWD',
    merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
    supportedNetworks: ['visa', 'masterCard', 'jcb'],
    total: {
      label: '車麻吉',
      amount,
      type: 'final',
    },
    requiredShippingContactFields: ['email'],
  };
};

export const applePayAvailable = (
  window,
): window is Window &
  typeof globalThis & { ApplePaySession: typeof ApplePaySession } => {
  return window.ApplePaySession && window.ApplePaySession.canMakePayments();
};

/**
 * ### creaetApplePaySession
 *
 * guest-aps apple pay
 * @param {{
 *  paymentInfo: { id: string; amount: string; };
 *  receipt: { [x: string]: string; }
 * }} data
 *
 * @param {{
 *    onError: (error: unknown) => void;
 *    onSuccess: (data: {
 *        id: string;
 *        parkinglot_name: string;
 *        parkinglot_id: string;
 *        checked_in_at: string;
 *        checked_out_at: string;
 *        amount: string;
 *        payment_state: 'paid' | 'pending' | 'unpaid';
 *        plate_number: string;
 *        created_at: string;
 *        is_prepaid: true;
 *        url: string;
 *        email: string;
 *        parking_ticket_id: string;
 *        should_leave_before: string;
 *        request_amount: string;
 *        discount_amount: string;
 * })     => void
 * }} callback
 *
 * @returns {Promise<void>}
 */

export const createApplePaySession = async (
  {
    paymentInfo,
    receipt,
  }: { paymentInfo: Pick<PaymentInfo, 'amount' | 'id'>; receipt: Receipt },
  callback,
) => {
  const { amount, id: paymentInfoId } = paymentInfo;
  if (!applePayAvailable(window)) {
    callback.onError({
      error: '無法使用 apple pay',
      ...paymentError.applePayUnavailable(),
    });
    return;
  }

  const applePaySession = new ApplePaySession(
    2,
    applePayConfig(amount) as ApplePayJS.ApplePayPaymentRequest,
  );
  let cancel;

  /**
   * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778021-onvalidatemerchant
   */
  applePaySession.onvalidatemerchant = async (e) => {
    try {
      const { data } = await Client.createApplePaySessionValidtion({
        transactionType: SERVICES.APS,
        sessionUrl: e.validationURL,
      });
      if (cancel) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      applePaySession.completeMerchantValidation((data as any).data);
    } catch (error) {
      applePaySession.completeMerchantValidation({});
      callback.onError({
        error,
        ...paymentError.applePayMerchantValidation(),
      });
    }
  };

  /**
   * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778020-onpaymentauthorized
   */
  applePaySession.onpaymentauthorized = async (e) => {
    const payment = e.payment;

    const { shippingContact } = payment;
    const { paymentData } = payment.token;

    try {
      const { data } = await Client.authApsApplePay({
        parkingTicketId: paymentInfoId,
        applePayPaymentInfo: JSON.stringify(paymentData),
        email: shippingContact.emailAddress,
        receipt,
      });
      applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);

      callback.onSuccess(data.data);
    } catch (error) {
      applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);

      callback.onError({
        error,
        ...paymentError.applePayPaymentAuthorized(error),
      });
    }
  };

  applePaySession.oncancel = (e) => {
    cancel = e;

    callback.onCancel({
      error: 'Apple Pay 交易已取消',
      ...paymentError.applePayCancel(),
    });

    return;
  };

  try {
    await applePaySession.begin();
  } catch (error) {
    if (error.message === 'Page already has an active payment session.') {
      return callback.onError({
        error,
        ...paymentError.applePayDuplicateOrders(),
      });
    }

    return callback.onError({ error, ...paymentError.applePayInit() });
  }
};
